'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useChart } from '@/contexts/chart-context';
import { OIL_CODE_COLORS, OIL_NAME_COLORS } from '@/constants/fuel-colors';
import { apiClient } from '@/lib/api-client';
import { Sales } from '@/lib/api/SalesRoute';
import { SalesInfoTreeMapItemDto } from '@/lib/api/data-contracts';
import HighchartsReact from 'highcharts-react-official';
import { useCallback, useEffect, useRef, useState } from 'react';

const initializeHighcharts = (Highcharts: any) => {
  if (!Highcharts) return;

  // Set global options
  Highcharts.setOptions({
    lang: {
      thousandsSep: ',',
      noData: '표시될 데이터가 없습니다.',
    },
  });
};

interface StoreData {
  [storeName: string]: {
    [productType: string]: number;
  };
}

const PRODUCT_NAMES = {
  premiumGasoline: '고급',
  gasoline: '무연',
  diesel: '경유',
  ureaSolution: '요소수',
  kerosene: '등유',
  butane: '부탄',
  propane: '프로판',
  other: '유외상품',
};

const ITEM_COLORS = [
  '#FF5733', // 빨강 계열
  '#33FF57', // 초록 계열
  '#5733FF', // 파랑 계열
  '#FFC300', // 노랑 계열
  '#DAF7A6', // 연한 초록 계열
  '#C70039', // 진한 빨강 계열
  '#900C3F', // 자주색 계열
  '#581845', // 보라 계열
  '#1F618D', // 어두운 파랑
  '#7DCEA0', // 옅은 초록
  '#F5B041', // 주황 계열
  '#48C9B0', // 청록 계열
  '#AF7AC5', // 라벤더 계열
  '#5DADE2', // 하늘색 계열
  '#E74C3C', // 밝은 빨강
  '#F7DC6F', // 밝은 노랑
  '#A569BD', // 보라 계열
  '#45B39D', // 청록 계열
  '#D4AC0D', // 금색 계열
  '#3498DB', // 푸른 계열
  '#F39C12', // 호박색
  '#1ABC9C', // 민트 계열
  '#2ECC71', // 녹색 계열
  '#E67E22', // 밝은 주황
  '#E74C3C', // 밝은 빨강
];

const wjpncColors: Record<string, string> = {
  ...OIL_CODE_COLORS,
  ...OIL_NAME_COLORS,
  고급: OIL_NAME_COLORS['고급무연'],
  // treemap에서 흰색(#ffffff)은 배경과 구분 불가하므로 별도 색상 사용
  '1002': '#0000FF',
  고급경유: '#0000FF',
};

interface StoreTreeMapProps {
  startDate: string;
  endDate: string;
}

export function StoreTreeMap({ startDate, endDate }: StoreTreeMapProps) {
  const { Highcharts, isHighchartsLoaded } = useChart();
  const [chartOptions, setChartOptions] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const chartRef = useRef<any>(null);

  const transformApiDataToStoreData = (
    apiData: SalesInfoTreeMapItemDto[]
  ): StoreData => {
    const storeData: StoreData = {};

    apiData.forEach(item => {
      const storeName = item.storeName || item.storeCd || '알 수 없음';
      storeData[storeName] = {};

      // Map API fields to our product types
      if (item.premiumGasoline)
        storeData[storeName]['premiumGasoline'] = item.premiumGasoline;
      if (item.gasoline) storeData[storeName]['gasoline'] = item.gasoline;
      if (item.diesel) storeData[storeName]['diesel'] = item.diesel;
      if (item.ureaSolution)
        storeData[storeName]['ureaSolution'] = item.ureaSolution;
      if (item.kerosene) storeData[storeName]['kerosene'] = item.kerosene;
      if (item.butane) storeData[storeName]['butane'] = item.butane;
      if (item.propane) storeData[storeName]['propane'] = item.propane;
      if (item.other) storeData[storeName]['other'] = item.other;
    });

    return storeData;
  };

  const createTreeMapData = (storesData: StoreData) => {
    const points: any[] = [];
    let totalValue = 0;
    let storeIndex = 0;

    for (const [storeName, products] of Object.entries(storesData)) {
      const storePoint = {
        id: storeName,
        name: storeName,
        color: ITEM_COLORS[storeIndex % ITEM_COLORS.length],
        pointType: 'store',
      };

      let storeValue = 0;

      for (const [productKey, value] of Object.entries(products)) {
        const productName =
          PRODUCT_NAMES[productKey as keyof typeof PRODUCT_NAMES] || productKey;
        const productPoint = {
          id: `${storeName}_${productKey}`,
          name: productName,
          parent: storeName,
          value: Math.round(value),
          pointType: 'product',
          parentColor: storePoint.color,
        };

        storeValue += productPoint.value;
        totalValue += productPoint.value;
        points.push(productPoint);
      }

      (storePoint as any).total = storeValue;
      points.push(storePoint);
      storeIndex++;
    }

    return { points, totalValue };
  };

  const createChartOptions = (
    points: any[],
    totalValue: number
  ): Highcharts.Options => {
    return {
      chart: {
        type: 'treemap',
      },
      title: {
        text: '',
        align: 'left',
      },
      subtitle: {
        text: '영역을 클릭하여 상세보기가 가능합니다. <i><b>% 는 소수점 둘째자리에서 반올림하여 총합이 100% 를 초과할 수도 있습니다.</b></i>',
        align: 'left',
        useHTML: true,
      },
      tooltip: {
        pointFormatter: function () {
          const point = this as any;
          let parentValue = totalValue;

          if (point.parent) {
            const parentPoint = points.find(p => p.id === point.parent);
            if (parentPoint) {
              parentValue = parentPoint.total;
            }
          }

          const percentage = (((point.value || 0) / parentValue) * 100).toFixed(
            2
          );
          return `<span style="color:${point.color}">●</span> <b>${point.name}</b><br/>${(point.value || 0).toLocaleString()}₩<br/>${percentage}%`;
        },
      },
      series: [
        {
          name: '주유소',
          type: 'treemap',
          layoutAlgorithm: 'squarified',
          // @ts-ignore
          allowDrillToNode: true,
          animationLimit: 1000,
          dataLabels: {
            enabled: false,
          },
          levels: [
            {
              level: 1,
              dataLabels: {
                enabled: true,
              },
              borderWidth: 3,
              // levelIsConstant: false
            },
            {
              level: 1,
              dataLabels: {
                style: {
                  fontSize: '14px',
                },
              },
            },
          ],
          data: points,
          events: {
            click: function (event: any) {
              const children = event.point.node.children;
              if (children.length) {
                children.forEach((child: any) => {
                  child.point.update({
                    color: wjpncColors[child.name as keyof typeof wjpncColors],
                  });
                });
              }
            },
          },
          breadcrumbs: {
            events: {
              click: function (button: any, breadcrumbs: any) {
                if (breadcrumbs.level === 0 && chartRef.current) {
                  // 레거시 코드와 동일한 로직으로 색상 초기화
                  chartRef.current.chart.series[0].data
                    .filter(
                      (d: any) =>
                        d.pointType === 'product' && d.color !== d.parentColor
                    )
                    .forEach((p: any) => {
                      p.point.update({
                        color: p.parentColor,
                      });
                    });
                }
              },
            },
          },
        },
      ],
      credits: {
        enabled: false,
      },
    };
  };

  const loadTreeMapData = useCallback(async () => {
    if (!isHighchartsLoaded || !Highcharts) return;

    try {
      setIsLoading(true);

      // API call to get sales statistics tree map data
      const response = await apiClient.get('sales/statistics/tree-map', {
        searchParams: {
          startDate,
          endDate,
        },
      });

      const apiResponse =
        (await response.json()) as Sales.GetSalesStatisticsTreeMap.ResponseBody;

      if (apiResponse.result && apiResponse.result.length > 0) {
        const storeData = transformApiDataToStoreData(apiResponse.result);
        const { points, totalValue } = createTreeMapData(storeData);

        // Update chart with real data
        const options = createChartOptions(points, totalValue);
        setChartOptions(options);
      } else {
        // No data available, show empty chart
        const options = createChartOptions([], 0);
        setChartOptions(options);
      }
    } catch (error) {
      console.error('Failed to load treemap data:', error);

      // Fallback to mock data on error
      const mockData: StoreData = {
        주유소A: {
          gasoline: 1500000,
          diesel: 1200000,
          premiumGasoline: 800000,
        },
        주유소B: {
          gasoline: 1800000,
          diesel: 1400000,
          premiumGasoline: 900000,
          kerosene: 300000,
        },
        주유소C: {
          gasoline: 1200000,
          diesel: 1000000,
          premiumGasoline: 700000,
          other: 200000,
        },
      };

      const { points, totalValue } = createTreeMapData(mockData);
      const options = createChartOptions(points, totalValue);
      setChartOptions(options);
    } finally {
      setIsLoading(false);
    }
  }, [isHighchartsLoaded, Highcharts, startDate, endDate]);

  useEffect(() => {
    if (isHighchartsLoaded && Highcharts) {
      initializeHighcharts(Highcharts);
      loadTreeMapData();
    }
  }, [isHighchartsLoaded, Highcharts, loadTreeMapData]);

  useEffect(() => {
    // 대시보드 새로고침 이벤트 리스너
    const handleRefresh = () => {
      if (isHighchartsLoaded && Highcharts) {
        loadTreeMapData();
      }
    };

    window.addEventListener('dashboard-refresh', handleRefresh);
    return () => window.removeEventListener('dashboard-refresh', handleRefresh);
  }, [loadTreeMapData, isHighchartsLoaded, Highcharts]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>주유소별 판매 통계</CardTitle>
      </CardHeader>
      <CardContent className='p-2'>
        <div>
          {!isHighchartsLoaded || isLoading ? (
            <div className='flex items-center justify-center h-full'>
              <div className='text-gray-500'>
                {!isHighchartsLoaded
                  ? 'Highcharts 초기화 중...'
                  : '데이터 로딩 중...'}
              </div>
            </div>
          ) : (
            <HighchartsReact
              ref={chartRef}
              highcharts={Highcharts}
              options={chartOptions}
              containerProps={{ style: { height: '100%' } }}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
